import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Cookies from 'universal-cookie';
import { Button } from '@material-ui/core';
import { useLogin } from './LoginContext';
import { DataGrid, esES, GridOverlay } from '@material-ui/data-grid';
import { ArrowLeft, CreditCard, CreditCardOff } from 'tabler-icons-react';
import { getUsuarios } from '../../services/cardService';
import ModalAsignarTarjeta from './ModalAsignarTarjeta';
import ModalEliminarTarjeta from './ModalEliminarTarjeta';
import '../../styles/tarjetas.css';

function CustomNoRowsOverlay() {
    return (
        <GridOverlay className="tarjetas-empty-root">
            <CreditCard size={48} color="#bdbdbd" />
            <div className="tarjetas-empty-label">No hay usuarios registrados</div>
        </GridOverlay>
    );
}

export default function TarjetasView({ onBack }) {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [modalAsignar, setModalAsignar] = useState(false);
    const [modalEliminar, setModalEliminar] = useState(false);

    const { tokenL } = useLogin();
    const token = tokenL?.token;
    const celda = parseInt(new Cookies().get('celda')) || 0;

    useEffect(() => {
        getUsuarios(token)
            .then((data) => {
                setUsuarios(
                    data.map((u) => ({
                        id: u.id,
                        usuario: u.userName,
                        nombre: u.nombre,
                        apellido: u.apellido,
                        tarjeta: u.tieneCard,
                    }))
                );
            })
            .finally(() => setLoading(false));
    }, [token]);

    const handleOpenAsignar = useCallback((user) => {
        setSelectedUser(user);
        setModalAsignar(true);
    }, []);

    const handleOpenEliminar = useCallback((user) => {
        setSelectedUser(user);
        setModalEliminar(true);
    }, []);

    const handleAsignarConfirm = useCallback((userId) => {
        setUsuarios((prev) =>
            prev.map((u) => (u.id === userId ? { ...u, tarjeta: 1 } : u))
        );
    }, []);

    const handleEliminarConfirm = useCallback((userId) => {
        setUsuarios((prev) =>
            prev.map((u) => (u.id === userId ? { ...u, tarjeta: 0 } : u))
        );
        setModalEliminar(false);
        setSelectedUser(null);
    }, []);

    const columns = useMemo(
        () => [
            {
                field: 'usuario',
                headerName: 'Usuario',
                flex: 1,
                minWidth: 100,
                headerAlign: 'center',
                align: 'center',
            },
            {
                field: 'nombre',
                headerName: 'Nombre',
                flex: 1.2,
                minWidth: 110,
                headerAlign: 'center',
                align: 'center',
            },
            {
                field: 'apellido',
                headerName: 'Apellido',
                flex: 1.2,
                minWidth: 110,
                headerAlign: 'center',
                align: 'center',
            },
            {
                field: 'tarjeta',
                headerName: 'Tarjeta',
                flex: 1,
                minWidth: 100,
                headerAlign: 'center',
                align: 'center',
                renderCell: ({ value }) =>
                    value ? (
                        <CreditCard size={22} color="var(--color-primary, #4c2e7f)" />
                    ) : (
                        <CreditCardOff size={22} color="#bdbdbd" />
                    ),
            },
            {
                field: 'acciones',
                headerName: 'Acciones',
                flex: 1.5,
                minWidth: 160,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                renderCell: ({ row }) => {
                    if (row.tarjeta === 0) {
                        return (
                            <Button
                                variant="contained"
                                className="table-button"
                                color="primary"
                                onClick={() => handleOpenAsignar(row)}
                            >
                                <h4 className="table-button-text">Asignar Tarjeta</h4>
                            </Button>
                        );
                    }
                    return (
                        <Button
                            variant="contained"
                            className="table-button tarjetas-btn--danger"
                            onClick={() => handleOpenEliminar(row)}
                        >
                            <h4 className="table-button-text">Eliminar Tarjeta</h4>
                        </Button>
                    );
                },
            },
        ],
        [handleOpenAsignar, handleOpenEliminar]
    );

    return (
        <div className="tarjetas-view">
            <div className="tarjetas-view-header">
                <Button
                    startIcon={<ArrowLeft size={20} />}
                    onClick={onBack}
                >
                    Regresar
                </Button>
                <h2 className="tarjetas-view-header-title">Gestión de Tarjetas RFID</h2>
            </div>

            <div className="tarjetas-view-table">
                <DataGrid
                    rows={usuarios}
                    columns={columns}
                    loading={loading}
                    localeText={esES.props.MuiDataGrid.localeText}
                    components={{ NoRowsOverlay: CustomNoRowsOverlay }}
                    disableColumnFilter
                    disableDensitySelector
                    hideFooterSelectedRowCount
                    rowHeight={52}
                />
            </div>

            <ModalAsignarTarjeta
                open={modalAsignar}
                usuario={selectedUser}
                token={token}
                celda={celda}
                onConfirm={(userId) => {
                    handleAsignarConfirm(userId);
                }}
                onCancel={() => {
                    setModalAsignar(false);
                    setSelectedUser(null);
                }}
            />

            <ModalEliminarTarjeta
                open={modalEliminar}
                usuario={selectedUser}
                token={token}
                onRemoveCard={handleEliminarConfirm}
                onCancel={() => {
                    setModalEliminar(false);
                    setSelectedUser(null);
                }}
            />
        </div>
    );
}
